import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeToggle } from './ThemeToggle';
import { LangSwitcher } from './LangSwitcher';
import type { AppLocale } from '../lib/locale';
import { pathWithLocale } from '../lib/locale';

export type AppOutletContext = { locale: AppLocale; basePath: string };

const NAV_ITEMS = [
  { segment: '', i18nKey: 'nav.home' as const },
  { segment: 'faq', i18nKey: 'nav.faq' as const },
  { segment: 'guide', i18nKey: 'nav.guide' as const },
  { segment: 'articles', i18nKey: 'nav.articles' as const },
  { segment: 'story', i18nKey: 'nav.story' as const },
  { segment: 'about', i18nKey: 'nav.about' as const },
  { segment: 'privacy', i18nKey: 'nav.privacy' as const },
  { segment: 'terms', i18nKey: 'nav.terms' as const },
  { segment: 'contact', i18nKey: 'nav.contact' as const },
];

export function AppLayout({ locale }: { locale: AppLocale }) {
  const { t } = useTranslation();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  const nav = useMemo(
    () =>
      NAV_ITEMS.map(({ segment, i18nKey }) => ({
        to:
          segment === '__auto__'
            ? `${pathWithLocale('/', locale)}#auto-search`
            : segment === 'articles'
              ? '/articles/'
              : pathWithLocale(segment ? `/${segment}` : '/', locale),
        label: t(i18nKey),
        i18nKey,
      })),
    [locale, t],
  );

  const homePath = pathWithLocale('/', locale);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (navOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [navOpen]);

  const basePath = locale === 'en' ? '' : `/${locale}`;

  return (
    <div className="app-shell">
      <div className="container">
        <div className="site-top-controls">
          <button
            type="button"
            className="nav-drawer__toggle"
            onClick={() => setNavOpen(true)}
            aria-expanded={navOpen}
            aria-controls="site-nav-drawer"
            aria-label={t('layout.openMenu')}
          >
            <span className="nav-drawer__toggle-bars" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
          <LangSwitcher locale={locale} />
          <div className="site-top-controls__spacer" aria-hidden="true" />
          <ThemeToggle title={t('layout.themeTitle')} />
        </div>

        <header className="site-header site-header--compact">
          <div className="site-title">{t('layout.siteTitle')}</div>
        </header>

        {navOpen && (
          <div
            className="nav-drawer-backdrop"
            onClick={() => setNavOpen(false)}
            aria-hidden="true"
          />
        )}

        <aside
          id="site-nav-drawer"
          className={`nav-drawer ${navOpen ? 'nav-drawer--open' : ''}`}
          aria-hidden={!navOpen}
          role="dialog"
          aria-modal="true"
          aria-label={t('layout.navLabel')}
        >
          <div className="nav-drawer__header">
            <span className="nav-drawer__title">{t('layout.menuTitle')}</span>
            <button
              type="button"
              className="nav-drawer__close"
              onClick={() => setNavOpen(false)}
              aria-label={t('layout.closeMenu')}
            >
              ×
            </button>
          </div>
          <nav className="site-nav site-nav--drawer" aria-label={t('layout.navLabel')}>
            {nav.map(({ to, label, i18nKey }) => (
              <NavLink
                key={i18nKey}
                to={to}
                end={to === homePath}
                className={({ isActive }) => (isActive ? 'active' : undefined)}
                onClick={() => setNavOpen(false)}
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <Outlet context={{ locale, basePath } satisfies AppOutletContext} />
        <footer className="site-footer">
          <p>{t('footer')}</p>
        </footer>
      </div>
    </div>
  );
}

